import styled from '@emotion/styled';
import { Col, Image, Row } from 'antd';
import { FaChartLine, FaCheck, FaExchangeAlt, FaShieldAlt } from 'react-icons/fa';
import { ORANGE, PRIMARY, YELLOW } from '../../shared/colors';
import { Layout } from '../shared/Layout';
import { Banner } from './components/Banner';
import { Contact } from './components/Contact';
import { Footer } from './components/Footer';
import { SECTION_1, SECURITY_POINTS } from './components/PageData/homePageData';

const Container = styled.div`
  background:
    linear-gradient(180deg, #f8fafc 0, #eef3f8 58%, #f8fafc 100%);

  .title {
    font-size: 13px;
    color: ${ORANGE};
    letter-spacing: 0.14em;
    text-transform: uppercase;
    margin-bottom: 24px;
    font-family: 'Poppins-B';
    text-align: center;
  }

  .title::after {
    content: '';
    background: ${ORANGE};
    height: 3px;
    width: 72px;
    margin-left: auto;
    margin-right: auto;
    margin-top: 5px;
    display: block;
    transform: translateY(1px);
    border-radius: 10px;
  }

  .home-body {
    max-width: 85%;
    margin: auto;
  }

  .section {
    color: var(--mt-text);
    display: flex;
    margin-bottom: 50px;
    padding-left: 8%;
    padding-right: 8%;

    > .text-description {
      margin-left: 0;
    }

    .sectionHead {
      font-size: 24px;
      font-weight: bold;
      font-style: Poppins-B;
      color: ${PRIMARY};
      margin-bottom: 18px;
    }
  }

  .intro-panel {
    background: white;
    border: 1px solid var(--mt-border);
    border-radius: 12px;
    box-shadow: 0 18px 44px rgba(5, 12, 18, 0.08);
    margin: -42px auto 56px;
    max-width: 1180px;
    padding: 26px;
    position: relative;
    z-index: 2;
  }

  .metric {
    border-right: 1px solid var(--mt-border);
    padding: 8px 24px;
  }

  .metric:last-child {
    border-right: 0;
  }

  .metric span {
    color: var(--mt-muted);
    display: block;
    font-family: Poppins-R;
    font-size: 12px;
    margin-bottom: 6px;
    text-transform: uppercase;
  }

  .metric strong {
    color: ${PRIMARY};
    font-family: Poppins-B;
    font-size: 22px;
  }

  .feature-card {
    background: white;
    border: 1px solid var(--mt-border);
    border-radius: 10px;
    box-shadow: 0 16px 36px rgba(5, 12, 18, 0.08);
    min-height: 190px;
    padding: 24px;
  }

  .feature-icon {
    align-items: center;
    background: linear-gradient(135deg, rgba(255, 79, 31, 0.14), rgba(255, 176, 0, 0.18));
    border: 1px solid rgba(255, 79, 31, 0.18);
    border-radius: 10px;
    color: ${ORANGE};
    display: flex;
    height: 42px;
    justify-content: center;
    margin-bottom: 18px;
    width: 42px;
  }

  .feature-card h3 {
    color: ${PRIMARY};
    font-family: Poppins-B;
    font-size: 18px;
    margin-bottom: 10px;
  }

  .feature-card p {
    color: var(--mt-muted);
    font-family: Poppins-R;
    line-height: 1.65;
    margin: 0;
  }

  .institutional-band {
    background:
      linear-gradient(90deg, rgba(255, 79, 31, 0.12), transparent 34%),
      ${PRIMARY};
    color: white;
    margin: 20px 0 54px;
    padding: 42px 8%;
  }

  .institutional-band h2 {
    color: white;
    font-family: Poppins-B;
    margin-bottom: 12px;
  }

  .institutional-band p {
    color: rgba(248, 250, 252, 0.72);
    font-family: Poppins-R;
    line-height: 1.7;
    margin: 0;
    max-width: 780px;
  }

  .market-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    justify-content: flex-end;
  }

  .market-tags span {
    border: 1px solid rgba(255, 176, 0, 0.38);
    border-radius: 999px;
    color: ${YELLOW};
    font-family: Poppins-B;
    padding: 8px 14px;
  }

  .brand-name {
    font-family: Poppins-B;
    font-size: 28px;
  }

  .description {
    color: #616161;
    font-family: Poppins-L;
    font-size: 16px;
    line-height: 1.65;
  }

  .check-list {
    color: #616161;
    font-family: Poppins-L;
    font-size: 16px;
  }

  .check-list p {
    margin-bottom: 16px;
  }

  .contact-image {
    display: flex;
    justify-content: center;
  }

  .contact-image .ant-image {
    max-width: 320px;
    width: 70%;
  }

  @media (max-width: 767px) {
    .title {
      margin-bottom: 24px;
    }

    .section {
      padding-left: 24px;
      padding-right: 24px;
      margin-bottom: 34px;
    }

    .intro-panel {
      margin: 0 18px 40px;
      padding: 18px;
    }

    .metric {
      border-right: 0;
      border-bottom: 1px solid var(--mt-border);
      padding: 14px 0;
    }

    .metric:last-child {
      border-bottom: 0;
    }

    .market-tags {
      justify-content: flex-start;
      margin-top: 18px;
    }

    .brand-name {
      display: block;
      font-size: 26px;
      text-align: center;
    }

    .sectionHead {
      margin-top: 18px;
      text-align: center;
    }

    .contact-image .ant-image {
      width: min(70vw, 260px);
    }
  }

`;

export const HomePage = () => {
  return (
    <Layout>
      <Container>
        <Banner />
        <div className='intro-panel'>
          <Row gutter={[0, 18]} justify='space-between'>
            <Col xs={24} md={8} className='metric'>
              <span>Titres suivis</span>
              <strong>Marché régional</strong>
            </Col>
            <Col xs={24} md={8} className='metric'>
              <span>Signaux marché</span>
              <strong>Bid / Ask en temps réel</strong>
            </Col>
            <Col xs={24} md={8} className='metric'>
              <span>Desk coverage</span>
              <strong>BEAC · BVMAC · BRVM</strong>
            </Col>
          </Row>
        </div>
        <div>
          <Row
            gutter={16}
            className='section'
            style={{ justifyContent: 'center', alignItems: 'center' }}
          >
            <Col>
              <h2
                className='title'
                style={{
                  marginTop: 20,
                }}
              >
                Une plateforme conçue pour les marchés obligataires
              </h2>
            </Col>
          </Row>
          <Row
            className='section'
            style={{
              justifyContent: 'space-evenly',
              alignItems: 'center',
              marginTop: 0,
            }}
          >
            <Col xs={24} md={10} xl={9}>
              <div>
                <span className='brand-name'>
                  <span style={{ color: ORANGE }}>Moranj</span>
                  <span style={{ color: PRIMARY }}>E</span>
                  <span style={{ color: YELLOW }}>Trade</span>
                </span>
                <p className='description'>
                  {' '}centralise la découverte des titres, les informations de
                  marché, la profondeur, les ordres et le suivi de portefeuille
                  dans une interface pensée pour les professionnels du marché
                  obligataire africain.
                </p>
              </div>
            </Col>
            <Col xs={24} md={10} xl={9}>
              <div className='text-description'>
                <p className='sectionHead'>Ce que le terminal apporte</p>
                <div className='check-list'>
                  {SECTION_1.map((sec, i) => (
                    <p key={i}>
                      <FaCheck color={ORANGE} style={{ marginBottom: -2 }} />{' '}
                      {sec}
                    </p>
                  ))}
                </div>
              </div>
            </Col>
          </Row>
          <Row gutter={[20, 20]} className='section' style={{ marginTop: 0 }}>
            <Col xs={24} md={8}>
              <div className='feature-card'>
                <div className='feature-icon'><FaChartLine /></div>
                <h3>Données et profondeur</h3>
                <p>
                  Une lecture rapide des prix, de la liquidité et des
                  instruments disponibles pour accélérer la décision.
                </p>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div className='feature-card'>
                <div className='feature-icon'><FaExchangeAlt /></div>
                <h3>Exécution structurée</h3>
                <p>
                  Des parcours d’achat et de vente alignés sur les besoins des
                  desks, avec suivi des ordres et transactions.
                </p>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div className='feature-card'>
                <div className='feature-icon'><FaShieldAlt /></div>
                <h3>Traçabilité et contrôle</h3>
                <p>
                  Une expérience sécurisée pour centraliser les portefeuilles,
                  les données clients et les opérations sensibles.
                </p>
              </div>
            </Col>
          </Row>
          <div className='institutional-band'>
            <Row gutter={[24, 18]} align='middle' justify='space-between'>
              <Col xs={24} md={14}>
                <h2>Couverture régionale, standards institutionnels.</h2>
                <p>
                  MoranjETrade s’adresse aux acteurs qui ont besoin d’une
                  plateforme claire, rapide et fiable pour suivre les marchés
                  obligataires, comparer les instruments et piloter l’activité.
                </p>
              </Col>
              <Col xs={24} md={10}>
                <div className='market-tags'>
                  <span>BEAC</span>
                  <span>BVMAC</span>
                  <span>BCEAO</span>
                  <span>BRVM</span>
                </div>
              </Col>
            </Row>
          </div>
          <Row
            gutter={16}
            className='section'
            style={{ justifyContent: 'center', alignItems: 'center' }}
          >
            <Col>
              <h2 className='title'>Contactez-nous</h2>
            </Col>
          </Row>
          <Row
            className='section'
            style={{
              justifyContent: 'space-evenly',
              alignItems: 'center',
              margin: 'auto',
            }}
          >
            <Col xs={24} sm={12} md={8} xl={8} className='contact-image'>
              <Image
                src='/assets/images/security.jpg'
                style={{ objectFit: 'contain', borderRadius: '50%' }}
                preview={false}
              />
            </Col>
            <Contact />
            <Col xs={24} sm={12} md={6} xl={6}>
              <div className='text-description'>
                <p className='sectionHead'>Votre sécurité, notre priorité</p>
                <div className='check-list'>
                  {SECURITY_POINTS.map((sec, i) => (
                    <p key={i}>
                      <FaCheck color={ORANGE} style={{ marginBottom: -2 }} />{' '}
                      {sec}
                    </p>
                  ))}
                </div>
              </div>
            </Col>
            {/* <Section title='Section 3' body={<p>Contenu 3</p>} /> */}
          </Row>
        </div>
        <Footer />
      </Container>
    </Layout>
  );
};
